import gql from 'graphql-tag';

const GET_CALENDAR_INTEGRATED = gql`
  query getLoggedInContext {
    getLoggedInContext {
      user {
        calendarIntegrated
      }
    }
  }
`;

const GET_CALENDAR_VIEW_EVENTS = gql`
  query getCalendarView($input: CalendarViewInput!) {
    getCalendarView(input: $input) {
      header {
        attribute
        value
      }
      events {
        eventId
        startTime
        endTime
        eventType
        allDay
        workItem {
          workId
          module
          displayId
          subject
          client {
            accountId
            name
          }
          site {
            id
            name
          }
        }
        appointment {
          sensitivity
          appointmentId
          subject
          appointmentSource
          syncStatus
          syncErrorReason
          recurringEvent
          address {
            name
            addressId
          }
          attendees {
            attendeeId
            user {
              userId
            }
          }
        }
      }
    }
  }
`;

const GET_APPOINTMENT = gql`
  query getAppointment($input: GetAppointmentInput!) {
    getAppointment(input: $input) {
      eventId
      startTime
      endTime
      eventType
      allDay
      workItem {
        workId
        module
        displayId
        subject
        client {
          accountId
          name
        }
      }
      appointment {
        sensitivity
        appointmentId
        subject
        appointmentSource
        syncStatus
        recurringEvent
        description
        address {
          addressId
          name
          line1
          line2
          line3
          city
          countryCode
          stateCode
          postalCode
        }
        attachments {
          fileName
          fileSize
          originalFileName
        }
        attendees {
          attendeeId
          user {
            userId
            name
            email
            accountType
          }
          acceptanceStatus
          attendeeType
        }
      }
    }
  }
`;

const GET_CALENDAR_SLA_DETAILS = gql`
  query($input: GetAggregatedValuesInput!) {
    getAggregatedValues(input: $input) {
      data
      hasMore
    }
  }
`;

const GET_TECH_GROUP = gql`
  query {
    getTechnicianGroups {
      groupId
      name
      description
      userList {
        userId
        name
        firstName
        lastName
        email
        role {
          roleId
          name
        }
        calendarIntegrated
      }
    }
  }
`;

const CREATE_APPOINTMENT = gql`
  mutation createAppointment($input: CreateAppointmentInput!) {
    createAppointment(input: $input) {
      eventId
      eventType
      startTime
      endTime
      allDay
      workItem {
        workId
        module
        subject
        client {
          accountId
          name
        }
      }
      appointment {
        appointmentId
        appointmentSource
        subject
        description
        syncStatus
        lastModifiedTime
        createdBy {
          userId
          name
          email
        }
        organizer {
          userId
          name
          email
        }
        recurringEvent
        address {
          addressId
          name
          line1
          postalCode
        }
        attendees {
          attendeeId
          attendeeType
          acceptanceStatus
          user {
            userId
            name
            email
          }
        }
        attachments {
          fileName
          fileSize
          originalFileName
        }
      }
    }
  }
`;

const UPDATE_APPOINTMENT = gql`
  mutation updateAppointment($input: UpdateAppointmentInput!) {
    updateAppointment(input: $input) {
      eventId
      eventType
      startTime
      endTime
      allDay
      workItem {
        workId
        module
        subject
        client {
          accountId
          name
        }
      }
      appointment {
        appointmentId
        appointmentSource
        subject
        description
        syncStatus
        lastModifiedTime
        createdBy {
          userId
          name
          email
        }
        organizer {
          userId
          name
          email
        }
        recurringEvent
        address {
          addressId
          name
          line1
          postalCode
        }
        attendees {
          attendeeId
          attendeeType
          acceptanceStatus
          user {
            userId
            name
            email
          }
        }
        attachments {
          fileName
          fileSize
          originalFileName
        }
      }
    }
  }
`;

const GET_ADDRESS_LIST = gql`
  query getAddresses($input: GetAddressInput!) {
    getAddresses(input: $input) {
      address {
        addressId
        name
        line1
        line2
        line3
        type
        city
        postalCode
        countryCode
        stateCode
      }
      listInfo {
        totalCount
        hasMore
      }
    }
  }
`;

const DELETE_APPOINTMENT = gql`
  mutation($input: AppointmentIdentifierInput!) {
    deleteAppointment(input: $input)
  }
`;

const RESYNC_APPOINTMENT = gql`
  mutation($input: AppointmentIdentifierInput!) {
    reSyncAppointment(input: $input) {
      eventId
    }
  }
`;

const CREATE_ADDRESS = gql`
  mutation createAddress($input: AddressInput!) {
    createAddress(input: $input) {
      addressId
      name
      line1
      line2
      line3
      type
      city
      postalCode
      countryCode
      stateCode
    }
  }
`;

const GET_APPOINTMENT_CALENDAR_EVENTS = gql`
  query getCalendarView($input: CalendarViewInput!) {
    getCalendarView(input: $input) {
      header {
        attribute
        value
      }
      events {
        eventId
        startTime
        endTime
        eventType
        allDay
        workItem {
          workId
          module
          displayId
          subject
          client {
            accountId
            name
          }
          site {
            id
            name
          }
        }
        appointment {
          sensitivity
          appointmentId
          subject
          appointmentSource
          syncStatus
          recurringEvent
          address {
            name
            addressId
          }
          attendees {
            attendeeId
            user {
              userId
              name
            }
          }
        }
      }
    }
  }
`;

const GET_PLACEHOLDER_TEMPLATES = gql`
  query getPlaceholderTemplates($constant: String!) {
    getPlaceholderTemplates(constant: $constant) {
      templateId
      title
      content
      placeholdersUsed
      constant
    }
  }
`;

const GET_SCHEDULING_EVENT_TEMPLATE = gql`
  query getPlaceholderTemplate($constant: String!) {
    getPlaceholderTemplate(constant: $constant) {
      content
      placeholdersUsed
      constant
    }
  }
`;

const UPDATE_SCHEDULING_EVENT_TEMPLATE = gql`
  mutation($input: UpdatePlaceholderTemplateInput!) {
    updatePlaceholderTemplate(input: $input) {
      content
      placeholdersUsed
      constant
    }
  }
`;

const GET_EVENT_TEMPLATE_META = gql`
  query getPlaceholderTemplateMetaInfo($constant: String!) {
    getPlaceholderTemplateMetaInfo(constant: $constant)
  }
`;

const GET_APPOINTMENT_EVENT_DESCRIPTION = gql`
  query getAppointmentEventDescription(
    $input: GetAppointmentEventDescriptionInput!
  ) {
    getAppointmentEventDescription(input: $input)
  }
`;

const GET_WIDGET_APPOINTMENT_COUNT = gql`
  query getCalendarView($input: CalendarViewInput!) {
    getCalendarView(input: $input) {
      events {
        eventId
        startTime
        endTime
        appointment {
          sensitivity
          appointmentId
          subject
        }
      }
    }
  }
`;

const GET_CALENDAR_INFO = gql`
  query getUserCalendarInfo {
    getUserCalendarInfo {
      calendarId
      calendarName
      integratedAt
      syncStatus
      syncErrorReason
      lastSyncTime
    }
  }
`;

const GET_SLA_TICKETS = gql`
  query getTicketList($listInfo: ListInfoInput!) {
    getTicketList(listInfo: $listInfo) {
      tickets {
        displayId
        subject
        resolutionDueTime
        resolutionViolated
        ticketId
      }
    }
  }
`;

const GET_TECHS_BY_CRITERIA = gql`
  query getTechnicianList($input: GetTechnicianListInput!) {
    getTechnicianList(input: $input) {
      userList {
        userId
        firstName
        lastName
        email
        name
        calendarIntegrated
        roles {
          roleId
          name
        }
      }
    }
  }
`;

const GET_TICKET_DETAILS_FOR_FORM = gql`
  query getTicket($id: ID!) {
    getTicket(id: $id) {
      ticketId
      client {
        name
        accountId
      }
      requester {
        name
        firstName
        lastName
        userId
      }
      technician {
        name
        userId
        email
        firstName
        lastName
        deletedTime
      }
      site {
        name
        id
        address {
          addressId
          name
        }
      }
      resolutionDueTimeLeft
      resolutionDueTime
      resolutionTime
      resolutionViolated
      resolutionPauseTime
      resolutionCode
    }
  }
`;

const GET_DISPATCHER_CALENDAR_EVENTS = gql`
  query getCalendarView($input: CalendarViewInput!) {
    getCalendarView(input: $input) {
      header {
        attribute
        value
      }
      events {
        eventId
        startTime
        endTime
        eventType
        allDay
        workItem {
          workId
          module
          displayId
          subject
          client {
            accountId
            name
          }
          site {
            id
            name
          }
          ticket {
            ticketId
            technician {
              userId
            }
          }
        }
        appointment {
          sensitivity
          appointmentId
          subject
          appointmentSource
          syncStatus
          syncErrorReason
          recurringEvent
          address {
            name
            addressId
          }
          attendees {
            attendeeId
            user {
              userId
            }
          }
        }
      }
    }
  }
`;

const RESYNC_CALENDAR_SUBSCRIPTION = gql`
  mutation {
    reSyncCalendarSubscription {
      syncStatus
    }
  }
`;

export {
  GET_CALENDAR_VIEW_EVENTS,
  GET_APPOINTMENT,
  GET_CALENDAR_SLA_DETAILS,
  GET_TECH_GROUP,
  CREATE_APPOINTMENT,
  UPDATE_APPOINTMENT,
  GET_ADDRESS_LIST,
  DELETE_APPOINTMENT,
  CREATE_ADDRESS,
  GET_APPOINTMENT_CALENDAR_EVENTS,
  GET_SCHEDULING_EVENT_TEMPLATE,
  UPDATE_SCHEDULING_EVENT_TEMPLATE,
  GET_EVENT_TEMPLATE_META,
  GET_APPOINTMENT_EVENT_DESCRIPTION,
  GET_WIDGET_APPOINTMENT_COUNT,
  GET_CALENDAR_INFO,
  GET_SLA_TICKETS,
  RESYNC_APPOINTMENT,
  GET_TECHS_BY_CRITERIA,
  GET_CALENDAR_INTEGRATED,
  GET_TICKET_DETAILS_FOR_FORM,
  GET_DISPATCHER_CALENDAR_EVENTS,
  RESYNC_CALENDAR_SUBSCRIPTION,
  GET_PLACEHOLDER_TEMPLATES
};
